// Sivana product page behaviour that Horizon's blocks don't cover. Vanilla, deferred, no dependencies.
// - "Size Chart" link next to the "Size:" legend, and the size guide dialog (tabs + body/garment toggle)
// - live's "PLEASE SELECT A SIZE" step: the preselected size counts only after the shopper clicks a size
// - "make it a set" pop-up after an add to bag
// - model fit line and photo label (which model, her height, the size she wears)
// - refreshes [data-sivana-refresh] elements (stock line, gallery badges) when the variant changes
(() => {
  const SELECT_EVENT = 'shopify:product:select';

  const sectionRoot = () => document.querySelector('.product-information')?.closest('.shopify-section');

  const sizeFieldset = (root) =>
    [...root.querySelectorAll('variant-picker fieldset.variant-option')].find((fieldset) => {
      const id = fieldset.dataset.optionId || '';
      const legend = fieldset.querySelector('legend')?.textContent.trim().toLowerCase() || '';
      return /-size$/.test(id) || legend.startsWith('size');
    });

  const sizeChoices = (fieldset) => (fieldset ? fieldset.querySelectorAll('input[type="radio"]').length : 0);
  const PHONE = window.matchMedia('(max-width: 749px)');

  // Put a copy of the "Size Chart" link inside the Size fieldset (Horizon re-renders the picker, so re-check).
  function placeSizeGuide(root) {
    const fieldset = sizeFieldset(root);
    if (!fieldset) return;
    if (!fieldset.hasAttribute('data-sivana-size-fieldset')) fieldset.setAttribute('data-sivana-size-fieldset', '');
    const source = root.querySelector('.sivana-size-guide > [data-sivana-size-open]');
    // Inside the legend, so it sits on the "Size:" line and can never overlap the size buttons.
    const legend = fieldset.querySelector(':scope > legend');
    const holder = legend || fieldset;
    if (source && !holder.querySelector(':scope > [data-sivana-size-open]')) {
      holder.appendChild(source.cloneNode(true));
    }
  }

  function setupSizeRequirement(root) {
    const fieldset = sizeFieldset(root);
    if (!fieldset || sizeChoices(fieldset) < 2) return;
    // A size carried over from another color's swatch is already chosen (blocks/sivana-pdp-first-paint.liquid)
    if (!root.hasAttribute('data-sivana-size-carried')) root.classList.add('sivana-size-pending');

    const stockBlock = root.querySelector('.sivana-pdp-stock-shipping');
    if (stockBlock && !stockBlock.querySelector('.sivana-size-warning')) {
      const warning = document.createElement('div');
      warning.className = 'sivana-size-warning';
      warning.setAttribute('role', 'alert');
      warning.textContent = 'PLEASE SELECT A SIZE';
      stockBlock.prepend(warning);
    }

    root.addEventListener('click', (event) => {
      const label = event.target.closest('fieldset[data-sivana-size-fieldset] .variant-option__button-label');
      if (label) root.classList.remove('sivana-size-pending', 'sivana-size-error');
    });

    // Listen on window in the capture phase so this runs before Horizon's own handlers: with no size
    // chosen, "Select Size" (main button or sticky bar) only brings the size picker into view, with
    // no add to cart and no fly-to-cart animation.
    window.addEventListener(
      'click',
      (event) => {
        if (!root.classList.contains('sivana-size-pending')) return;
        const button = event.target.closest('.product-information .add-to-cart-button, sticky-add-to-cart .add-to-cart-button');
        if (!button) return;
        const sticky = !!button.closest('sticky-add-to-cart');
        if (!sticky && !root.contains(button)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (sticky && PHONE.matches) {
          openSizeSheet(root);
          return;
        }
        root.classList.add('sivana-size-error');
        if (sticky || button.dataset.puppet === 'true') {
          delete button.dataset.puppet;
          button.blur();
          fieldset.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      },
      true
    );
  }

  // Desktop details column taller than the screen: it scrolls with the page until its bottom is
  // visible, then holds; scrolling back up reveals the top again. Shorter columns just pin at 140px.
  // Desktop: the photo column sticks when it is shorter than the buy column. A gallery taller than the
  // screen scrolls normally until its last photo reaches the bottom of the screen, then holds there,
  // so it keeps company with the buy column down to the end of the section.
  function setupStickyMedia(root) {
    const media = root.querySelector('.product-information__grid .product-information__media');
    if (!media) return;
    const desktop = window.matchMedia('(min-width: 990px)');
    const TOP = 140;
    const GAP = 24;
    const update = () => {
      if (!desktop.matches) {
        media.style.removeProperty('--sivana-media-top');
        return;
      }
      const wrapper = document.querySelector('.page-wrapper');
      const viewport = wrapper && getComputedStyle(wrapper).overflowY === 'auto' ? wrapper.clientHeight : window.innerHeight;
      const top = Math.min(TOP, viewport - media.offsetHeight - GAP);
      media.style.setProperty('--sivana-media-top', `${Math.round(top)}px`);
    };
    new ResizeObserver(update).observe(media);
    window.addEventListener('resize', update);
    desktop.addEventListener('change', update);
    update();
  }

  function setupStickyDetails(root) {
    const details = root.querySelector('.product-details.sticky-content--desktop');
    if (!details) return;
    const desktop = window.matchMedia('(min-width: 990px)');
    const TOP = 140;
    const GAP = 24;
    let scroller = window;
    let last = 0;
    let top = TOP;
    let queued = false;

    const scrollerFor = () => {
      const wrapper = document.querySelector('.page-wrapper');
      return wrapper && getComputedStyle(wrapper).overflowY === 'auto' ? wrapper : window;
    };
    const position = () => (scroller === window ? window.scrollY : scroller.scrollTop);
    const viewport = () => (scroller === window ? window.innerHeight : scroller.clientHeight);

    function update() {
      queued = false;
      if (!desktop.matches) {
        details.style.removeProperty('top');
        return;
      }
      const minTop = Math.min(TOP, viewport() - details.offsetHeight - GAP);
      const y = position();
      top = Math.max(minTop, Math.min(TOP, top - (y - last)));
      last = y;
      details.style.top = `${Math.round(top)}px`;
    }
    const onScroll = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(update);
    };
    function bind() {
      if (scroller) scroller.removeEventListener('scroll', onScroll);
      scroller = scrollerFor();
      scroller.addEventListener('scroll', onScroll, { passive: true });
      last = position();
      top = TOP;
      update();
    }
    bind();
    desktop.addEventListener('change', bind);
    window.addEventListener('resize', onScroll, { passive: true });
    new ResizeObserver(onScroll).observe(details);
    // The accordion scrolls the page frame by frame while a section opens; move the column in the
    // same frame so the two never drift apart.
    document.addEventListener('sivana:scrolled', update);
  }

  function setupDialog() {
    document.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-sivana-size-open]');
      if (opener) {
        // Outfit pages have one guide per piece: open the one beside the link. The copy on the
        // "Size:" line sits outside its guide, so it falls back to the page's guide.
        const dialog =
          opener.closest('.sivana-size-guide')?.querySelector('[data-sivana-size-dialog]') ||
          document.querySelector('[data-sivana-size-dialog]');
        if (dialog && !dialog.open) dialog.showModal();
        return;
      }
      const dialog = event.target.closest('[data-sivana-size-dialog]');
      if (!dialog) return;
      if (event.target === dialog || event.target.closest('[data-sivana-size-close]')) {
        dialog.close();
        return;
      }
      const tab = event.target.closest('[data-sivana-size-tab]');
      if (tab) {
        dialog.querySelectorAll('[data-sivana-size-tab]').forEach((other) => {
          other.setAttribute('aria-selected', String(other === tab));
        });
        dialog.querySelectorAll('[data-sivana-size-panel]').forEach((panel) => {
          panel.hidden = panel.dataset.sivanaSizePanel !== tab.dataset.sivanaSizeTab;
        });
        return;
      }
      const toggle = event.target.closest('[data-sivana-size-table]');
      if (toggle) {
        dialog.querySelectorAll('[data-sivana-size-table]').forEach((other) => {
          other.setAttribute('aria-pressed', String(other === toggle));
        });
        dialog.querySelectorAll('[data-sivana-size-table-panel]').forEach((panel) => {
          panel.hidden = panel.dataset.sivanaSizeTablePanel !== toggle.dataset.sivanaSizeTable;
        });
      }
    });

    // Live shows one table at a time on desktop (with the toggle) and both stacked on mobile.
    const desktop = window.matchMedia('(min-width: 701px)');
    const syncTables = () => {
      document.querySelectorAll('[data-sivana-size-dialog]').forEach((dialog) => {
        if (!dialog.querySelector('[data-sivana-size-toggle]')) return;
        const active = dialog.querySelector('[data-sivana-size-table][aria-pressed="true"]')?.dataset.sivanaSizeTable;
        dialog.querySelectorAll('[data-sivana-size-table-panel]').forEach((panel) => {
          panel.hidden = desktop.matches && panel.dataset.sivanaSizeTablePanel !== active;
        });
      });
    };
    desktop.addEventListener('change', syncTables);
    syncTables();
  }

  // Swap our server-rendered bits when Horizon loads a new variant.
  function setupRefresh(root) {
    root.addEventListener(SELECT_EVENT, (event) => {
      event.promise
        ?.then(({ detail }) => {
          const html = detail?.html;
          if (!html) return;
          root.querySelectorAll('[data-sivana-refresh]').forEach((element) => {
            const key = element.getAttribute('data-sivana-refresh');
            const fresh = html.querySelector(`[data-sivana-refresh="${key}"]`);
            if (fresh) element.innerHTML = fresh.innerHTML;
          });
          document.dispatchEvent(new CustomEvent('sivana:pdp-refreshed'));
        })
        .catch(() => {});
    });
  }

  // Button labels for screen readers follow the size state: "Select size" until a size is picked.
  // (Horizon's phone sticky bar button has no announceable text of its own.)
  // The price she pays, as shown (just the amount, without the screen-reader "Sale price" words).
  const priceText = (root) =>
    (root.querySelector('.product-information .product-details product-price .price')?.textContent || '').match(/[$€£][\d.,]+|[\d.,]+\s?[$€£]/)?.[0] || '';

  // Phones: the floating Add to Bag (Horizon's sticky bar, kept on screen from the start by CSS) reads
  // "Select size · $29.95" until a size is picked, then "Add to bag · $29.95" ("Pre-order · $29.95" for preorders).
  function syncFloatingLabel(root) {
    const button = root.querySelector('sticky-add-to-cart .sticky-add-to-cart__button');
    if (!button) return;
    const price = priceText(root);
    if (button.dataset.sivanaAdded === 'true') return;
    const pending = root.classList.contains('sivana-size-pending');
    const action = pending ? 'Select size' : isPreorder(root) ? 'Pre-order' : 'Add to bag';
    const label = `${action}${price ? ` · ${price}` : ''}`;
    if (button.dataset.sivanaLabel !== label) button.dataset.sivanaLabel = label;
  }

  // Phones: a small sheet to pick a size without scrolling back up. Picking a size there picks it on the
  // page (so price, stock and photos follow), then adds to the bag.
  let sheet;
  const SHEET_ADDED_SEEN_MS = 900;
  // After an add the sheet slides back down instead of vanishing (assets/sivana-pdp-main.css).
  function slideSheetAway() {
    if (!sheet?.open) return;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return sheet.close();
    sheet.classList.add('is-closing');
    const finish = () => {
      sheet.removeEventListener('animationend', onEnd);
      if (!sheet.classList.contains('is-closing')) return;
      sheet.classList.remove('is-closing');
      sheet.close();
    };
    // Only the sheet's own slide (the button's check mark animates inside it too)
    const onEnd = (event) => event.target === sheet && finish();
    sheet.addEventListener('animationend', onEnd);
    setTimeout(finish, 400);
  }
  function openSizeSheet(root) {
    const fieldset = sizeFieldset(root);
    if (!fieldset) return;
    if (!sheet) {
      sheet = document.createElement('dialog');
      sheet.className = 'sivana-size-sheet';
      sheet.setAttribute('aria-label', 'Choose a size');
      sheet.innerHTML = `
        <div class="sivana-size-sheet__grab" aria-hidden="true"></div>
        <div class="sivana-size-sheet__top"><img alt="" data-img><p><span data-title></span><small data-sub></small></p></div>
        <p class="sivana-size-sheet__label">Size</p>
        <div class="sivana-size-sheet__sizes" role="radiogroup" aria-label="Size" data-sizes></div>
        <p class="sivana-size-sheet__fit" data-fit hidden></p>
        <button type="button" class="sivana-size-sheet__add" data-add data-sv-atc-button disabled>Select a size</button>`;
      document.body.append(sheet);
      sheet.addEventListener('click', (event) => {
        if (event.target === sheet) sheet.close();
      });
    }
    const color = [...root.querySelectorAll('variant-picker fieldset.variant-option')]
      .find((candidate) => candidate !== fieldset && /colou?r/i.test(candidate.querySelector('legend')?.textContent || ''))
      ?.querySelector('input:checked')?.value;
    const price = priceText(root);
    const image = root.querySelector('.product-information__media img');
    sheet.querySelector('[data-img]').src = image?.currentSrc || image?.src || '';
    sheet.querySelector('[data-title]').textContent = root.querySelector('.sivana-pdp-title__heading')?.firstChild?.textContent.trim() || '';
    sheet.querySelector('[data-sub]').textContent = [color, price].filter(Boolean).join(' · ');
    const fit = root.querySelector('.sivana-model-fit__line')?.textContent.trim();
    const fitLine = sheet.querySelector('[data-fit]');
    fitLine.textContent = fit || '';
    fitLine.hidden = !fit;

    const add = sheet.querySelector('[data-add]');
    const holder = sheet.querySelector('[data-sizes]');
    let chosen = null;
    let busy = false;
    holder.innerHTML = '';
    add.disabled = true;
    add.textContent = 'Select a size';
    fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
      const option = document.createElement('button');
      option.type = 'button';
      option.className = 'sivana-size-sheet__size';
      option.textContent = input.value;
      option.setAttribute('role', 'radio');
      option.setAttribute('aria-checked', 'false');
      option.disabled = input.dataset.optionAvailable === 'false';
      option.addEventListener('click', () => {
        chosen = input;
        holder.querySelectorAll('[role="radio"]').forEach((other) => other.setAttribute('aria-checked', String(other === option)));
        add.disabled = false;
        add.textContent = 'Add to bag';
      });
      holder.append(option);
    });
    // The sheet stays open while the bag updates: its own button plays the Adding / Added animation
    // (assets/sivana-atc.js, it was the button pressed), and the sheet slides away once the check has
    // had a moment on screen, like the quick add pop-up.
    add.onclick = () => {
      if (!chosen || busy) return;
      busy = true;
      // "Adding" starts now, while the page switches to the chosen size; it ends with the add itself.
      let settle;
      const outcome = new Promise((resolve) => (settle = resolve));
      add.dispatchEvent(new CustomEvent('sivana:atc-pending', { bubbles: true, detail: { promise: outcome } }));
      const done = () => {
        busy = false;
      };
      // Never leave the button stuck if no add happens
      const stuck = setTimeout(() => {
        settle({ detail: { didError: true } });
        done();
      }, 8000);
      const added = new Promise((resolve) => add.addEventListener('sivana:atc-added', resolve, { once: true }));
      document.addEventListener(
        'shopify:cart:lines-update',
        (event) => {
          clearTimeout(stuck);
          Promise.resolve(event.promise).then(settle, () => settle({ detail: { didError: true } }));
          Promise.resolve(event.promise)
            .then((result) => {
              // Failed: after the button's shake, the sheet slides away so the reason (the note at the
              // top) can be read.
              if (result?.detail?.didError) {
                done();
                setTimeout(slideSheetAway, 450);
                return;
              }
              // Backup in case the animation script isn't there
              return Promise.race([added, new Promise((resolve) => setTimeout(resolve, 1500))])
                .then(() => new Promise((resolve) => setTimeout(resolve, SHEET_ADDED_SEEN_MS)))
                .then(() => {
                  done();
                  slideSheetAway();
                });
            })
            .catch(done);
        },
        { once: true }
      );
      const form = root.querySelector('.product-information form input[name="id"]');
      const before = form?.value;
      chosen.closest('label')?.click();
      // Wait for Horizon to switch the variant, then add with the floating button.
      const started = Date.now();
      const tryAdd = () => {
        const now = root.querySelector('.product-information form input[name="id"]')?.value;
        if ((now && now !== before) || chosen.checked || Date.now() - started > 1500) {
          requestAnimationFrame(() => root.querySelector('sticky-add-to-cart .sticky-add-to-cart__button')?.click());
        } else {
          setTimeout(tryAdd, 25);
        }
      };
      tryAdd();
    };
    sheet.showModal();
  }

  // Phones: if an add fails, put the floating button's label back. The Adding / Added animation itself
  // (and the buzz) comes from assets/sivana-atc.js, on every add-to-bag button.
  function showAdded(root, ok) {
    const button = root.querySelector('sticky-add-to-cart .sticky-add-to-cart__button');
    if (!PHONE.matches || !button || ok) return;
    delete button.dataset.sivanaAdded;
    syncFloatingLabel(root);
  }

  // After an add, a short "Added to bag" note drops from the top with a Checkout button (phones: full
  // width under the header; desktop: a compact card at the top right).
  // Skipped when the "Make it a set" pop-up is about to open, since it confirms the add itself.
  const NOTE_AFTER_ADDED_MS = 450;
  function setupAddedNote(root) {
    let note;
    let timer;

    const noteElement = () => {
      if (!note) {
        note = document.createElement('div');
        note.className = 'sivana-added-note';
        note.setAttribute('role', 'status');
        note.innerHTML = '<img alt="" data-img><p><span data-head>Added to bag</span><small data-sub></small></p><a href="/checkout" class="sivana-added-note__checkout">Checkout</a>';
        document.body.append(note);
        note.addEventListener('click', (clickEvent) => {
          if (!clickEvent.target.closest('a')) note.classList.remove('is-shown');
        });
      }
      return note;
    };

    const show = (duration) => {
      note.classList.add('is-shown');
      clearTimeout(timer);
      timer = setTimeout(() => note.classList.remove('is-shown'), duration);
    };

    const showNote = () => {
      noteElement().classList.remove('is-error');
      const image = root.querySelector('.product-information__media img');
      note.querySelector('[data-img]').src = image?.currentSrc || image?.src || '';
      note.querySelector('[data-head]').textContent = 'Added to bag';
      const title = root.querySelector('.sivana-pdp-title__heading')?.firstChild?.textContent.trim() || '';
      // Color and size as picked ("Almond / S"). The color comes from the sticky bar's variant name,
      // the size from the size buttons (the sticky bar can still show the color alone).
      const size = sizeFieldset(root)?.querySelector('input:checked')?.value || '';
      const named = root.querySelector('sticky-add-to-cart .sticky-add-to-cart__variant')?.textContent.trim() || '';
      const parts = named.split(' / ').filter((part) => part && part !== size);
      const variant = [...parts, size].filter(Boolean).join(' / ');
      note.querySelector('[data-sub]').textContent = [title, variant].filter(Boolean).join(' · ');
      // A set from Build your set (assets/sivana-build-your-set.js) names its own colors and photo.
      const set = window.sivanaBysNote;
      if (set && Date.now() - set.at < 5000) {
        if (set.image) note.querySelector('[data-img]').src = set.image;
        note.querySelector('[data-sub]').textContent = set.text;
      }
      show(3200);
    };

    const showProblem = (message) => {
      noteElement().classList.add('is-error');
      note.querySelector('[data-head]').textContent = "Couldn't add to bag";
      note.querySelector('[data-sub]').textContent = message || 'Please try again.';
      show(5000);
    };

    document.addEventListener('shopify:cart:lines-update', (event) => {
      if (event.action && event.action !== 'add') return;
      // The "Make it a set" pop-up confirms the first add of a visit itself; don't stack the note on it.
      const offer = document.querySelector('[data-sivana-set-offer]');
      let offerSeen = true;
      try {
        offerSeen = sessionStorage.getItem('sivana:set-offer-seen') === '1';
      } catch (error) {
        // Storage blocked: show the note.
      }

      // Confirm at once; undo below if Shopify says no (sold out, quantity limit, network trouble).
      showAdded(root, true);
      // The note drops in after the button has said "Added" (assets/sivana-atc.js fires sivana:atc-added),
      // with a short beat so the check is seen first. From a pop-up (phone size sheet, quick add) it waits
      // until the pop-up has gone, so it isn't shown dimmed behind it.
      let cancelled = false;
      if (!(Boolean(offer) && !offerSeen) && !offer?.open) {
        let shown = false;
        const afterAdded = (addedEvent) => {
          if (shown) return;
          shown = true;
          document.removeEventListener('sivana:atc-added', afterAdded);
          const popup = addedEvent?.target?.closest?.('dialog[open]');
          const show = () => !cancelled && showNote();
          if (popup) popup.addEventListener('close', show, { once: true });
          else setTimeout(show, NOTE_AFTER_ADDED_MS);
        };
        document.addEventListener('sivana:atc-added', afterAdded);
        // Backup in case the animation script isn't there
        Promise.resolve(event.promise)
          .then(() => setTimeout(afterAdded, 2000))
          .catch(() => {});
      }

      let settled = false;
      const fail = (message) => {
        if (settled) return;
        settled = true;
        cancelled = true;
        document.removeEventListener('shopify:cart:error', onError);
        showAdded(root, false);
        showProblem(message);
      };
      // Shopify's readable reason is in the description ("You can't add more ... to the cart.");
      // the message itself is just "Cart Error".
      const onError = (errorEvent) => {
        const reason = errorEvent.detail?.description || errorEvent.description;
        fail(typeof reason === 'string' && reason ? reason : '');
      };
      document.addEventListener('shopify:cart:error', onError);
      Promise.resolve(event.promise)
        .then((result) => {
          if (result?.detail?.didError) fail();
          settled = true;
          document.removeEventListener('shopify:cart:error', onError);
        })
        .catch(() => fail());
    });
  }

  // Preorders: blocks/sivana-pdp-stock-shipping.liquid marks the selected variant with [data-sivana-preorder].
  // Stoq puts the offer's shipping message in the product form as a cart line property (its name follows the
  // offer's settings, for example properties[Preorder]); show the message under the button word for word.
  // Our own properties start with an underscore and are skipped.
  const isPreorder = (root) => !!root.querySelector('[data-sivana-preorder]');
  function stoqMessage(root) {
    const form = root.querySelector('form[action*="/cart/add"]');
    for (const input of form ? form.querySelectorAll('input[name^="properties["]') : []) {
      if (!input.name.startsWith('properties[_') && input.value.trim()) return input.value.trim();
    }
    return '';
  }
  // The ship window from the Stoq message, the same rules as the cart's snippets/sivana-preorder-window.liquid:
  // - a set date wins: "Ships December 15" -> "Dec 15"; two dates ("Dec 1 to Dec 15") -> "Dec 1 to Dec 15"
  // - otherwise a length of time anywhere in it ("Please allow 4 to 8 weeks for delivery") -> "Nov 24 to Dec 22",
  //   counted from today. Also "3 weeks", "10-14 days", "1 to 2 months" (a month counts as 30 days).
  // No match returns '' and the message shows word for word.
  const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
  function preorderWindow(message) {
    const dates = [...message.matchAll(/\b(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|june?|july?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\.?\s+(\d{1,2})(?:st|nd|rd|th)?\b/gi)]
      .filter((found) => +found[2] >= 1 && +found[2] <= 31)
      .slice(0, 2)
      .map((found) => {
        const month = MONTHS.indexOf(found[1].slice(0, 3).toLowerCase());
        return new Date(2000, month, +found[2]).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      });
    if (dates.length) return dates.length > 1 && dates[0] !== dates[1] ? `${dates[0]} to ${dates[1]}` : dates[0];
    const match = message.match(/(\d+)\s*(?:(?:to|-|–)\s*(\d+)\s*)?(day|week|month)s?\b/i);
    if (!match) return '';
    const perUnit = { day: 1, week: 7, month: 30 }[match[3].toLowerCase()];
    const date = (count) => {
      const day = new Date();
      day.setDate(day.getDate() + count * perUnit);
      return day.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };
    const first = date(+match[1]);
    const last = match[2] ? date(+match[2]) : first;
    return first === last ? first : `${first} to ${last}`;
  }
  function syncPreorderDate(root) {
    const line = root.querySelector('[data-sivana-preorder-ship]');
    if (!line) return;
    const message = stoqMessage(root);
    const shipWindow = preorderWindow(message);
    const value = shipWindow || message;
    if (line.textContent !== value) line.textContent = value;
    if (line.hidden !== !value) line.hidden = !value;
    const label = root.querySelector('[data-sivana-preorder-label]');
    const wanted = shipWindow ? 'Estimated to ship' : 'Pre-order';
    if (label && label.textContent !== wanted) label.textContent = wanted;
    const prefix = root.querySelector('[data-sivana-preorder-prefix]');
    if (prefix && prefix.hidden !== !shipWindow) prefix.hidden = !shipWindow;
  }

  // Stoq's "I acknowledge ... preorder terms" checkbox (on when the offer asks for it) lands in the sticky bar,
  // off screen, so shoppers only met it in Stoq's pop-up after pressing Pre-order. Keep it right above the
  // page's button so they can tick it first (Stoq's pop-up still catches anyone who doesn't).
  // Stoq checks it by id, so moving it is safe; when Stoq draws a new one (variant change), the old one goes.
  function placeStoqTerms(root) {
    const buttons = root.querySelector('.product-information .product-details .product-form-buttons');
    if (!buttons) return;
    root.querySelectorAll('.restock-rocket-acknowledgement-checkbox').forEach((terms) => {
      if (terms.nextElementSibling === buttons) return;
      const placed = buttons.previousElementSibling;
      if (placed?.classList.contains('restock-rocket-acknowledgement-checkbox')) placed.remove();
      buttons.before(terms);
    });
  }

  function syncButtonLabels(root) {
    syncFloatingLabel(root);
    syncPreorderDate(root);
    placeStoqTerms(root);
    const pending = root.classList.contains('sivana-size-pending');
    const preorder = isPreorder(root);
    const text = root.querySelector('.product-information .add-to-cart-text__content')?.textContent.trim() || 'Add to bag';
    root.querySelectorAll('.add-to-cart-button').forEach((button) => {
      const sticky = !!button.closest('sticky-add-to-cart');
      const wanted = pending ? 'Select size' : preorder ? 'Pre-order' : sticky ? text : null;
      if (wanted === null) {
        if (button.hasAttribute('aria-label')) button.removeAttribute('aria-label');
      } else if (button.getAttribute('aria-label') !== wanted) {
        button.setAttribute('aria-label', wanted);
      }
    });
    // Before a size is chosen, the sticky bar names only the color ("Mocha", not "Mocha / S").
    const variant = root.querySelector('sticky-add-to-cart .sticky-add-to-cart__variant');
    if (variant && pending) {
      const sizeLike = /^(xxs|xs|s|m|l|xl|xxl|2xl|3xl|1x|2x|3x|s\/m|m\/l|l\/xl|one size|os|\d+)$/i;
      const parts = variant.textContent.trim().split(' / ');
      const shown = parts.filter((part) => !sizeLike.test(part.trim())).join(' / ');
      if (parts.length > 1 && shown && variant.textContent !== shown) variant.textContent = shown;
    }
  }

  function watchButtonLabels(root) {
    syncButtonLabels(root);
    // Stoq sets the Shipping message's value after a variant change without changing the page's structure
    root.addEventListener('change', () => setTimeout(() => syncButtonLabels(root), 400));
    new MutationObserver(() => syncButtonLabels(root)).observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  // Review highlight: copy two short 5-star quotes from the Judge.me widget (loaded further down).
  function setupReviewHighlight() {
    const box = document.querySelector('[data-sivana-review-highlight]');
    const reviews = document.querySelector('#sivana-reviews');
    if (!box || !reviews) return;
    const list = box.querySelector('[data-quotes]');
    const max = Number(box.dataset.max || 2);
    if (!list || max < 1) return;

    const shorten = (text) => {
      const clean = text.replace(/\s+/g, ' ').trim();
      if (clean.length <= 130) return clean;
      const cut = clean.slice(0, 130);
      return `${cut.slice(0, cut.lastIndexOf(' '))}…`;
    };
    // One look for every quote: sentence case. Shouted (ALL CAPS) and Title Case reviews are lowered
    // first; then each sentence starts with a capital, "I" stays a capital (I'm, I've...), and a few
    // proper names keep theirs. Reviews already written normally are left as they are.
    const PROPER = ['Sivana', 'California', 'Florida', 'Italy', 'Christmas', 'Thanksgiving'];
    const sentenceCase = (text) => {
      const letters = text.replace(/[^A-Za-z]/g, '');
      const upper = letters.replace(/[^A-Z]/g, '').length;
      const words = text.match(/[A-Za-z][A-Za-z']*/g) || [];
      const titled = words.filter((word) => /^[A-Z]/.test(word)).length;
      let out = text;
      if ((letters.length && upper / letters.length > 0.6) || (words.length > 3 && titled / words.length > 0.6)) {
        out = out.toLowerCase();
      }
      out = out.replace(/(^\s*|[.!?…]\s+)([a-z])/g, (match, lead, letter) => lead + letter.toUpperCase());
      out = out.replace(/\bi(?=\b|'[a-z]+\b)/g, 'I');
      PROPER.forEach((name) => {
        out = out.replace(new RegExp(`\\b${name}\\b`, 'gi'), name);
      });
      return out;
    };
    const shortName = (name) => {
      const parts = name.trim().split(/\s+/).filter(Boolean);
      if (!parts.length) return '';
      const first = parts[0].charAt(0).toUpperCase() + parts[0].slice(1).toLowerCase();
      return parts.length > 1 ? `${first} ${parts[parts.length - 1][0].toUpperCase()}.` : first;
    };

    function fill() {
      const items = [...reviews.querySelectorAll('.jm-review-item')];
      if (!items.length) return false;
      const picks = items
        .filter((item) => /^5 out of 5/.test(item.querySelector('.jm-star-rating')?.getAttribute('aria-label') || ''))
        .map((item) => ({
          text: item.querySelector('.jdgm-review-content__body-content')?.textContent || '',
          name: item.querySelector('.jm-reviewer-info__name')?.textContent || '',
        }))
        .filter((review) => review.text.trim().length >= 25)
        .slice(0, max);
      if (!picks.length) return true;
      list.replaceChildren(
        ...picks.map((review) => {
          const li = document.createElement('li');
          const quote = document.createElement('p');
          quote.className = 'sivana-review-highlight__quote';
          quote.textContent = `“${shorten(sentenceCase(review.text.replace(/\s+/g, ' ').trim()))}”`;
          const who = document.createElement('p');
          who.className = 'sivana-review-highlight__name';
          who.textContent = shortName(review.name) || 'Verified buyer';
          li.append(quote, who);
          return li;
        })
      );
      list.hidden = false;
      return true;
    }

    if (fill()) return;
    const observer = new MutationObserver(() => {
      if (fill()) observer.disconnect();
    });
    observer.observe(reviews, { childList: true, subtree: true });
    setTimeout(() => observer.disconnect(), 20000);
  }

  // Accordion: smooth open/close, one section at a time, like Eileen Fisher's PDP. When an opening
  // section reaches the bottom of the screen, the page moves up with it in the same frames, so the
  // column grows upward in one motion instead of growing down and then scrolling back.
  function setupAccordions() {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const scroller = () => {
      const wrapper = document.querySelector('.page-wrapper');
      return wrapper && getComputedStyle(wrapper).overflowY === 'auto' ? wrapper : window;
    };
    const viewportHeight = (el) => (el === window ? window.innerHeight : el.clientHeight);

    document.querySelectorAll('.sivana-pdp-accordion').forEach((accordion) => {
      const items = [...accordion.querySelectorAll('details.sivana-pdp-accordion__item')];
      items.forEach((item) => item.removeAttribute('name'));

      const animate = (item, from, to, done) => {
        const body = item.querySelector('.sivana-pdp-accordion__body');
        if (reduceMotion.matches || !body.animate) {
          done();
          return;
        }
        item.dataset.animating = 'true';
        body.style.overflow = 'hidden';
        const animation = body.animate(
          [
            { height: `${from}px`, opacity: from ? 1 : 0 },
            { height: `${to}px`, opacity: to ? 1 : 0 },
          ],
          { duration: 320, easing: 'cubic-bezier(0.2, 0.7, 0.2, 1)' }
        );
        animation.onfinish = () => {
          body.style.overflow = '';
          delete item.dataset.animating;
          done();
        };
      };

      // While a section opens, keep its bottom edge from going below the screen: each frame, scroll
      // the page up by exactly the amount that would overflow (never so far that the section's own
      // heading leaves the top). Growth and scroll happen in the same frame, so nothing bounces.
      const follow = (item) => {
        const el = scroller();
        const body = item.querySelector('.sivana-pdp-accordion__body');
        const heading = item.querySelector('summary');
        const step = () => {
          const overflow = body.getBoundingClientRect().bottom + 24 - viewportHeight(el);
          const room = heading.getBoundingClientRect().top - 120;
          const by = Math.min(overflow, room);
          if (by > 0.5) {
            // 'instant': the page has smooth scrolling on, which would lag behind the animation.
            el.scrollBy({ top: by, behavior: 'instant' });
            document.dispatchEvent(new Event('sivana:scrolled'));
          }
          if (item.dataset.animating) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      };

      const open = (item) => {
        item.open = true;
        const height = item.querySelector('.sivana-pdp-accordion__body').scrollHeight;
        animate(item, 0, height, () => {});
        follow(item);
      };
      const close = (item) => {
        const height = item.querySelector('.sivana-pdp-accordion__body').offsetHeight;
        animate(item, height, 0, () => {
          item.open = false;
        });
        if (reduceMotion.matches) item.open = false;
      };

      items.forEach((item) => {
        item.querySelector('summary').addEventListener('click', (event) => {
          event.preventDefault();
          if (item.dataset.animating) return;
          if (item.open) {
            close(item);
          } else {
            items.filter((other) => other !== item && other.open).forEach(close);
            open(item);
          }
        });
      });
    });
  }

  // Model fit ("Anna is 5'9" and wears size S"): a line under the sizes and a label on the first photo,
  // from the product's model looks (blocks/sivana-pdp-model-fit.liquid). Picks the look that matches the
  // selected color, else one without a color, else the first.
  function setupModelFit(root) {
    const box = root.querySelector('[data-sivana-model-fit]');
    if (!box) return;
    let looks = [];
    try {
      looks = JSON.parse(box.querySelector('[data-looks]').textContent).filter((look) => look.name && look.height && look.size);
    } catch (error) {
      return;
    }
    if (!looks.length) return;
    const selectedColor = () => {
      const fieldset = [...root.querySelectorAll('variant-picker fieldset.variant-option')].find((candidate) =>
        /colou?r/i.test(candidate.querySelector('legend')?.textContent || '')
      );
      return (fieldset?.querySelector('input:checked')?.value || '').trim().toLowerCase();
    };
    const render = () => {
      const color = selectedColor();
      const look =
        looks.find((candidate) => candidate.color && candidate.color.trim().toLowerCase() === color) ||
        looks.find((candidate) => !candidate.color) ||
        looks[0];
      const line = box.querySelector('[data-line]');
      const text = `${look.name} is ${look.height} and wears size ${look.size}.`;
      if (line.textContent !== text) line.textContent = text;
      if (look.measurements) line.title = look.measurements;

      const first = [
        ...root.querySelectorAll('.product-information__media .media-gallery__grid > li, .product-information__media slideshow-slide'),
      ].find((item) => item.offsetWidth > 0);
      root.querySelectorAll('.sivana-model-chip').forEach((chip) => {
        if (chip.parentElement !== first) chip.remove();
      });
      if (!first) return;
      let chip = first.querySelector(':scope > .sivana-model-chip');
      if (!chip) {
        chip = document.createElement('span');
        chip.className = 'sivana-model-chip';
        if (getComputedStyle(first).position === 'static') first.style.position = 'relative';
        first.appendChild(chip);
      }
      const label = `${look.name} · ${look.height} · wears ${look.size}`;
      if (chip.textContent !== label) chip.textContent = label;
    };
    render();
    document.addEventListener(SELECT_EVENT, () => setTimeout(render, 60));
    root.addEventListener('change', () => setTimeout(render, 60));
    const media = root.querySelector('.product-information__media');
    if (media) {
      new MutationObserver(() => requestAnimationFrame(render)).observe(media, {
        attributes: true,
        subtree: true,
        attributeFilter: ['hidden', 'data-sivana-filtered'],
      });
    }
  }

  // Item number (SKU): shown at the end of the Description accordion instead of under the price. Horizon's
  // own SKU block stays in the page, hidden, and keeps updating with the variant; copy its text across.
  function syncSku(root) {
    const target = root.querySelector('[data-sivana-sku]');
    const source = root.querySelector('product-sku-component');
    if (!target || !source) return;
    const sku = source.textContent.replace(/^\s*sku[:\s]*/i, '').trim();
    const span = target.querySelector('span');
    if (span.textContent !== sku) span.textContent = sku;
    target.hidden = !sku;
  }

  // "Make it a set" pop-up (blocks/sivana-pdp-set-offer.liquid): after this product is added to the bag,
  // once per visit, suggest building a set. "Build my set" opens the set builder with this piece in slot 1.
  function setupSetOffer(root) {
    const dialog = root.querySelector('[data-sivana-set-offer]') || document.querySelector('[data-sivana-set-offer]');
    if (!dialog || typeof dialog.showModal !== 'function') return;
    const SEEN = 'sivana:set-offer-seen';
    const seen = () => {
      try {
        return sessionStorage.getItem(SEEN) === '1';
      } catch (error) {
        return false;
      }
    };
    const markSeen = () => {
      try {
        sessionStorage.setItem(SEEN, '1');
      } catch (error) {
        // Storage blocked: the pop-up may show again, which is harmless.
      }
    };
    const close = () => dialog.open && dialog.close();
    dialog.querySelectorAll('[data-close]').forEach((button) => button.addEventListener('click', close));
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) close();
    });
    dialog.querySelector('[data-build]')?.addEventListener('click', () => {
      const variantId = root.querySelector('.product-information form input[name="id"]')?.value;
      const link = dialog.querySelector('[data-build]');
      if (variantId) link.href = `${dialog.dataset.builderUrl}${dialog.dataset.builderUrl.includes('?') ? '&' : '?'}start=${variantId}`;
    });
    document.addEventListener('shopify:cart:lines-update', (event) => {
      if (event.action && event.action !== 'add') return;
      if (seen()) return;
      Promise.resolve(event.promise)
        .then((result) => {
          const detail = result?.detail || {};
          if (detail.didError) return;
          if (detail.productId && String(detail.productId) !== dialog.dataset.productId) return;
          markSeen();
          // Desktop: let the cart drawer open first; the pop-up sits on top of it.
          setTimeout(() => {
            if (!dialog.open) dialog.showModal();
          }, PHONE.matches ? 900 : 450); // phones: after the button's Added check has played
        })
        .catch(() => {});
    });
  }

  // Fabric panel (blocks/sivana-pdp-fabric.liquid + snippets/sivana-fabric-panel.liquid): the buy box
  // "Fabric" line opens a side panel (a bottom sheet on phones) with the fabric's details.
  function setupFabricPanel() {
    document.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-sivana-fabric-open]');
      if (opener) {
        const panel = document.getElementById(opener.dataset.sivanaFabricOpen);
        if (panel && !panel.open) panel.showModal();
        return;
      }
      const panel = event.target.closest('[data-sivana-fabric-panel]');
      if (!panel) return;
      if (event.target === panel || event.target.closest('[data-sivana-fabric-close]')) panel.close();
    });
  }

  function init() {
    const root = sectionRoot();
    if (!root) return;
    placeSizeGuide(root);
    setupSizeRequirement(root);
    watchButtonLabels(root);
    setupStickyDetails(root);
    setupStickyMedia(root);
    setupReviewHighlight();
    setupModelFit(root);
    setupSetOffer(root);
    setupAddedNote(root);
    setupFabricPanel();
    setupAccordions();
    setupDialog();
    setupRefresh(root);
    syncSku(root);
    document.addEventListener(SELECT_EVENT, () => setTimeout(() => syncSku(root), 300));

    const picker = root.querySelector('.product-details') || root;
    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        placeSizeGuide(root);
        syncSku(root);
      });
    }).observe(picker, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
